import { z } from "zod";
import type { Json } from "@/types/database";

export const equipmentCategories = ["machine", "hand_tool", "ppe", "material", "other"] as const;
export type EquipmentCategory = (typeof equipmentCategories)[number];

export const phaseEquipmentItemSchema = z.object({
  name: z.string().trim(),
  note: z.string().optional(),
  category: z.enum(equipmentCategories).optional()
});

export type PhaseEquipmentItem = z.infer<typeof phaseEquipmentItemSchema>;

/** Külső JSON-ból: szinonimák és ismeretlen értékek → engedélyezett enum. */
export function normalizeEquipmentCategory(c: unknown): EquipmentCategory | undefined {
  if (c === null || c === undefined || c === "") return undefined;
  const s = String(c).trim();
  if (s === "tool") return "hand_tool";
  if (s === "digital_tool") return "other";
  if ((equipmentCategories as readonly string[]).includes(s)) return s as EquipmentCategory;
  return "other";
}

export const phaseEquipmentSchema = z.object({
  narrative: z.string(),
  items: z.array(phaseEquipmentItemSchema)
});

/** Full long-form markdown (or plain text) per section; `equipment` also has structured `items` for the hub. */
export const phaseGuideSchema = z.object({
  goal: z.string(),
  workflow: z.string(),
  cautions: z.string(),
  duration: z.string(),
  equipment: phaseEquipmentSchema,
  technical_details: z.string(),
  common_mistakes: z.string(),
  project_specific_notes: z.string(),
  materials: z.string(),
  checklist: z.string(),
  photos_to_take: z.string(),
  inspection_points: z.string(),
  /** Optional ordering hint inside the playbook (separate from DB `order_index`). */
  sequence_order: z.number().int().nullable(),
  /** Cross-references: section hashes, titles, or free lines. */
  related_sections: z.array(z.string())
});

export type PhaseGuide = z.infer<typeof phaseGuideSchema>;

export const emptyPhaseGuide = (): PhaseGuide => ({
  goal: "",
  workflow: "",
  cautions: "",
  duration: "",
  equipment: { narrative: "", items: [] },
  technical_details: "",
  common_mistakes: "",
  project_specific_notes: "",
  materials: "",
  checklist: "",
  photos_to_take: "",
  inspection_points: "",
  sequence_order: null,
  related_sections: []
});

function parseSequenceOrder(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v === "number" && Number.isFinite(v)) return Math.trunc(v);
  if (typeof v === "string" && v.trim() !== "") {
    const n = parseInt(v, 10);
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

function parseRelatedSections(v: unknown): string[] {
  if (Array.isArray(v)) {
    return v
      .filter((x): x is string => typeof x === "string")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  if (typeof v === "string") {
    return v
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
}

function str(v: unknown, fallback = ""): string {
  return typeof v === "string" ? v : fallback;
}

/** Normalize DB json: legacy string equipment → narrative only */
const looseEquipmentItem = z.object({
  name: z.string().optional().default(""),
  note: z.string().optional(),
  category: z.unknown().optional()
});

const looseEquipment = z.object({
  narrative: z.string().optional().default(""),
  items: z.array(looseEquipmentItem).optional().default([])
});

const looseGuide = z.object({
  goal: z.string().optional().default(""),
  workflow: z.string().optional().default(""),
  cautions: z.string().optional().default(""),
  duration: z.string().optional().default(""),
  equipment: looseEquipment.optional(),
  technical_details: z.string().optional().default(""),
  common_mistakes: z.string().optional().default(""),
  project_specific_notes: z.string().optional().default(""),
  materials: z.string().optional().default(""),
  checklist: z.string().optional().default(""),
  photos_to_take: z.string().optional().default(""),
  inspection_points: z.string().optional().default(""),
  sequence_order: z.unknown().optional(),
  related_sections: z.unknown().optional()
});

export function parsePhaseGuide(raw: Json | null | undefined): PhaseGuide {
  if (raw === null || raw === undefined) return emptyPhaseGuide();
  const base = typeof raw === "object" && raw !== null && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};

  let equipment: unknown = base.equipment;
  if (typeof equipment === "string") {
    equipment = { narrative: equipment, items: [] };
  }
  if (equipment === null || equipment === undefined) {
    equipment = { narrative: "", items: [] };
  }

  const merged = {
    goal: str(base.goal),
    workflow: str(base.workflow),
    cautions: str(base.cautions),
    duration: str(base.duration),
    equipment,
    technical_details: str(base.technical_details),
    common_mistakes: str(base.common_mistakes),
    project_specific_notes: str(base.project_specific_notes),
    materials: str(base.materials),
    checklist: str(base.checklist),
    photos_to_take: str(base.photos_to_take),
    inspection_points: str(base.inspection_points),
    sequence_order: base.sequence_order,
    related_sections: base.related_sections
  };

  const loose = looseGuide.safeParse(merged);
  if (!loose.success) return emptyPhaseGuide();
  const l = loose.data;
  const items = sanitizeEquipmentItems(
    (l.equipment?.items ?? []).map((row) => ({
      name: row.name ?? "",
      note: row.note,
      category: normalizeEquipmentCategory(row.category)
    }))
  );
  return phaseGuideSchema.parse({
    goal: l.goal,
    workflow: l.workflow,
    cautions: l.cautions,
    duration: l.duration,
    equipment: {
      narrative: l.equipment?.narrative ?? "",
      items
    },
    technical_details: l.technical_details,
    common_mistakes: l.common_mistakes,
    project_specific_notes: l.project_specific_notes,
    materials: l.materials,
    checklist: l.checklist,
    photos_to_take: l.photos_to_take,
    inspection_points: l.inspection_points,
    sequence_order: parseSequenceOrder(l.sequence_order),
    related_sections: parseRelatedSections(l.related_sections)
  });
}

export function sanitizeEquipmentItems(items: PhaseEquipmentItem[]): PhaseEquipmentItem[] {
  return items
    .map((row) => ({
      name: row.name.trim(),
      note: row.note?.trim() || undefined,
      category: normalizeEquipmentCategory(row.category)
    }))
    .filter((row) => row.name.length > 0);
}

export function phaseGuideToJson(guide: PhaseGuide): Json {
  const cleaned: PhaseGuide = {
    ...guide,
    equipment: {
      narrative: guide.equipment.narrative,
      items: sanitizeEquipmentItems(guide.equipment.items)
    },
    related_sections: guide.related_sections.map((s) => s.trim()).filter(Boolean)
  };
  return cleaned;
}

export function wordCount(text: string): number {
  const t = text.trim();
  if (!t) return 0;
  return t.split(/\s+/).length;
}
